import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";
import { Fragment, ReactNode, memo, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { getAndroidSafeAreaTopPaddingCss, isAndroidWebView } from "src/utils/mobile";

const DialogComponent = memo(
  ({
    open,
    toggleDialog,
    type,
    setOpenCustomModal,
    children,
    hideCloseIcon,
  }: {
    open: boolean;
    type?: string;
    toggleDialog: (t: boolean) => void;
    setOpenCustomModal?: (t: boolean) => void;
    children: ReactNode;
    hideCloseIcon?: boolean;
  }) => {
    const scrollTopRef = useRef<any | null>(null);
    const initialFocusRef = useRef<HTMLDivElement | null>(null);
    const androidTopPadding = useMemo(() => {
      if (!isAndroidWebView()) return undefined;
      return getAndroidSafeAreaTopPaddingCss(24);
    }, []);

    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={(e) => false}
          initialFocus={initialFocusRef}
        >
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          </Transition.Child>

          <div className="flex fixed inset-0 z-10 justify-center items-center w-screen">
            <div
              className={twMerge(
                "relative flex mx-auto items-center justify-center min-h-full w-full md:max-w-[calc(100vw-20%)] tablet:max-w-[calc(100vw-5%)] text-center sm:items-center",
                type === "news" && "w-full"
              )}
            >
              <Transition.Child
                as="div"
                className="relative flex flex-row items-center justify-center w-full"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="absolute top-[24px] tablet:top-3 right-3 z-50 md:top-0 md:-right-10">
                  <button
                    type="button"
                    className={twMerge(
                      "relative text-gray-500 border-none rounded-md bg-none md:text-white hover:text-gray-500 focus-visible:border-none focus-visible:outline-none",
                      hideCloseIcon && "opacity-0 cursor-default"
                    )}
                    onClick={() => {
                      if (type && type === "add_card") {
                        setOpenCustomModal && setOpenCustomModal(true);
                      } else {
                        toggleDialog(false);
                      }
                    }}
                  >
                    <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                  </button>
                </div>
                <Dialog.Panel
                  ref={scrollTopRef}
                  className={twMerge(
                    scrollTopRef.current ? "" : "", //gradient-scroll
                    `myScrollableDiv flex flex-col relative p-[24px] tablet:p-10 scrollbar-none tablet:scrollbar scrollbar-thumb-rounded-lg scrollbar-w-2 scrollbar-thumb-[#D1D5DB] h-[100vh] tablet:h-auto max-h-[100vh] tablet:max-h-[calc(100vh-81px)] overflow-y-auto text-left bg-white tablet:rounded-lg shadow-xl w-full tablet:w-[calc(100vw-20%)]`
                  )}
                  style={androidTopPadding ? { paddingTop: androidTopPadding } : undefined}
                >
                  {/* Prevent HeadlessUI from auto-focusing the first input (e.g. Datepicker opens on focus) */}
                  <div tabIndex={-1} ref={initialFocusRef} />
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }
);

export default DialogComponent;

export const Modal = memo(
  ({
    open,
    toggleDialog,
    zIndex = 50,
    children,
    modalClassName,
    hideXMarkIcon,
    closeOnBackdrop,
    autoClose,
    onAutoClose,
    allowIOSScroll = false,
  }: {
    open: boolean;
    zIndex?: number;
    toggleDialog: (t: boolean) => void;
    children: ReactNode;
    modalClassName?: string;
    hideXMarkIcon?: boolean;
    closeOnBackdrop?: boolean;
    autoClose?: number;
    onAutoClose?: () => void;
    allowIOSScroll?: boolean;
  }) => {
    // const [openCancelModal, setOpenCancelModal] = useState(false)
    const initialFocusRef = useRef<HTMLDivElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (!open || !autoClose) return;

      timerRef.current = setTimeout(() => {
        onAutoClose?.();
        toggleDialog(false);
      }, autoClose);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [open, autoClose, onAutoClose, toggleDialog]);

    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: zIndex }}
          onClose={() => (closeOnBackdrop ? toggleDialog(false) : false)}
          initialFocus={initialFocusRef}
        >
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          </Transition.Child>

          <div className="flex fixed inset-0 z-10 justify-center items-center w-screen">
            <div className="relative flex mx-auto items-center justify-center min-h-full md:max-w-[calc(100vw-20%)] max-w-[calc(100vw-5%)] text-center sm:items-center ">
              <Transition.Child
                as="div"
                className="flex relative flex-row justify-center items-center w-full"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="absolute top-3 right-3 z-50 md:top-0 md:-right-10" style={{ zIndex: zIndex }}>
                  <button
                    type="button"
                    className={twMerge(
                      "relative text-gray-500 border-none rounded-md bg-none md:text-white hover:text-gray-500 focus-visible:border-none focus-visible:outline-none",
                      hideXMarkIcon && "opacity-0 cursor-default relative left-[-9999px]"
                    )}
                    onClick={() => toggleDialog(false)}
                  >
                    <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                  </button>
                </div>
                <Dialog.Panel
                  onTouchMoveCapture={
                    allowIOSScroll
                      ? (e) => e.stopPropagation()
                      : undefined
                  }
                  className={twMerge(
                    `flex flex-col relative py-6 sm:py-10 min-h-[355px] w-full sm:min-w-[600px] text-left bg-white rounded-lg shadow-xl sm:w-[calc(100vw-20%)] [&_footer]:flex-col-reverse [&_footer]:items-stretch [&_footer]:gap-2 [&_footer>*]:w-full sm:[&_footer]:flex-row sm:[&_footer]:items-center sm:[&_footer]:gap-0 sm:[&_footer>*]:w-auto`,
                    modalClassName
                  )}
                >
                  {/* Prevent HeadlessUI from auto-focusing the first input (e.g. Datepicker opens on focus) */}
                  <div tabIndex={-1} ref={initialFocusRef} />
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }
);

const drawerStyle = cva("absolute top-0 right-0 h-full", {
  variants: {
    percentage: {
      "93": "w-full tablet:w-[93%]",
      "87": "w-full tablet:w-[87%]",
      "79": "w-full tablet:w-[79%]",
    },
  },
  defaultVariants: {
    percentage: "93",
  },
});

export type DrawerPercentageType = "93" | "87" | "79";

type DrawerPropsType = {
  open: boolean;
  toggleDrawer: (t: boolean) => void;
  percentage?: DrawerPercentageType;
  children: ReactNode;
  zIndex?: number;
  setOpenCustomModal?: (t: boolean) => void;
  hideXMarkIcon?: boolean;
};

export const ClosePanelBtnMobile = (
  {
    hideXMarkIcon = false,
    setOpenCustomModal,
    toggleDrawer,
    classes = '',
  }: {
    hideXMarkIcon: boolean,
    setOpenCustomModal?: (t: boolean) => void,
    toggleDrawer: (t: boolean) => void,
    classes?: string
  }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (setOpenCustomModal) setOpenCustomModal(true);
        else toggleDrawer(false);
        }}
      type="button"
      className={twMerge(
        "relative text-gray-500 border-none bg-none md:text-white focus-visible:outline-none",
        classes,
        hideXMarkIcon && "opacity-0 cursor-default relative left-[-9999px]"
      )}
      >
      <XMarkIcon className="w-12 h-12 sm:w-6 sm:h-6" aria-hidden="true" />
    </button>
  )
}

export const DrawerComponent = memo(
  ({
    open,
    toggleDrawer,
    percentage = "93",
    children,
    zIndex = 50,
    setOpenCustomModal,
    hideXMarkIcon = false,
  }: DrawerPropsType) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const initialFocusRef = useRef<HTMLDivElement | null>(null);
    const androidTopPadding = useMemo(() => {
      if (!isAndroidWebView()) return undefined;
      return getAndroidSafeAreaTopPaddingCss(0);
    }, []);
    const androidCloseBtnTop = useMemo(() => {
      if (!isAndroidWebView()) return undefined;
      return getAndroidSafeAreaTopPaddingCss(20);
    }, []);

    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 overflow-hidden"
          style={{ zIndex: zIndex }}
          onClose={(e) => false}
          initialFocus={initialFocusRef}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            {(state) => {
              return (
                <div className={twMerge(drawerStyle({ percentage: percentage }), "flex flex-col overflow-hidden")}>
                  <div
                    className="absolute top-[20px] right-6 sm:-left-8 sm:top-2.5 z-60"
                    style={androidCloseBtnTop ? { top: androidCloseBtnTop } : undefined}
                  >
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (setOpenCustomModal) setOpenCustomModal(true);
                        else toggleDrawer(false);
                      }}
                      type="button"
                      className={twMerge(
                        "relative text-gray-500 border-none bg-none md:text-white focus-visible:outline-none",
                        hideXMarkIcon && "opacity-0 cursor-default relative left-[-9999px]"
                      )}
                    >
                      <XMarkIcon className="w-12 h-12 sm:w-6 sm:h-6" aria-hidden="true" />
                    </button> */}
                    <ClosePanelBtnMobile toggleDrawer={toggleDrawer} hideXMarkIcon={false} classes="hidden sm:inline-block"/>
                  </div>
                  <div
                    ref={contentRef}
                    className={twMerge(
                      // iOS Safari: скроллер не должен быть сам себе height:100dvh и не должен использовать
                      // устаревший `-webkit-overflow-scrolling: touch` внутри `position: fixed` — из-за этого
                      // ломается одно-пальцевый скролл. Высоту задаёт flex-родитель (flex-1 + min-h-0).
                      "flex-1 min-h-0 overflow-y-auto overflow-x-hidden tablet:overflow-x-auto bg-white shadow-xl overscroll-contain"
                    )}
                    style={androidTopPadding ? { paddingTop: androidTopPadding } : undefined}
                  >
                    <div className="flex flex-col h-full min-h-screen min-h-[100dvh] pb-[env(safe-area-inset-bottom)]">
                      {/* Prevent HeadlessUI from auto-focusing the first input (e.g. Datepicker opens on focus) */}
                      <div tabIndex={-1} ref={initialFocusRef} />
                      {children}
                    </div>
                  </div>
                </div>
              );
            }}
          </Transition.Child>
        </Dialog>
      </Transition.Root>
    );
  }
);

export const NonModalDrawer = memo(
  ({ open, toggleDrawer, percentage = "93", children, zIndex = 50, hideXMarkIcon = false }: DrawerPropsType) => {
    const androidTopPadding = useMemo(() => {
      if (!isAndroidWebView()) return undefined;
      return getAndroidSafeAreaTopPaddingCss(0);
    }, []);
    const androidCloseBtnTop = useMemo(() => {
      if (!isAndroidWebView()) return undefined;
      return getAndroidSafeAreaTopPaddingCss(10);
    }, []);
    return (
      <Transition show={open} as={Fragment}>
        <Transition.Child
          as={Fragment}
          enter="transform transition ease-in-out duration-300"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transform transition ease-in-out duration-300"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div
            role="complementary"
            aria-modal={false}
            style={{ zIndex }}
            className={twMerge(drawerStyle({ percentage }))}
          >
            <div className="absolute -left-8 top-2.5">
              <button
                type="button"
                onClick={() => toggleDrawer(false)}
                className={twMerge(
                  "relative text-gray-500 border-none bg-none focus-visible:outline-none",
                  hideXMarkIcon && "opacity-0 cursor-default"
                )}
                style={androidCloseBtnTop ? { top: androidCloseBtnTop } : undefined}
              >
                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>

            <div
              className="overflow-y-auto h-full bg-white shadow-xl"
              style={androidTopPadding ? { paddingTop: androidTopPadding } : undefined}
            >
              <div className="flex flex-col h-full min-h-screen">{children}</div>
            </div>
          </div>
        </Transition.Child>
      </Transition>
    );
  }
);
